import getApiInstance from '../../common/apiInstance';
import { getApiErrorMessage } from '../../common/apiErrorMessage';
import type { ImportJobDto } from 'endchess-contracts';
import type { MoveQualityCounts, MoveQuality } from 'endchess-contracts';
import type { ExtendedGameSource } from '../../common/gameSource';
import type { ReplayGameSummary } from '../../replay/replayApi';
import { GAMES_BROWSE_PAGE_SIZE } from '../shared/gamesPageConstants';

const gamesApi = getApiInstance('/games', 'no-settings');
const importApi = getApiInstance('/import', 'no-settings');

export type GameAnalysisStatus =
  | 'pending'
  | 'ready'
  | 'none'
  | 'scheduled';

export type MyGameSummary = ReplayGameSummary & {
  analysisStatus: GameAnalysisStatus;
  moveQualityCounts?: MoveQualityCounts;
  source: ExtendedGameSource;
};

export type MistakePly = {
  plyIndex: number;
  fen: string;
  playedUci: string;
  playedSan: string;
  diff?: number;
  setupEvalCp?: number;
  quality?: string;
  bestUci?: string;
  bestSan?: string;
  refutationUci?: string;
  refutationSan?: string;
};

export type MyGameReplayPayload = {
  gameId: string;
  white: string;
  black: string;
  whiteElo: number;
  blackElo: number;
  result: string;
  date?: string;
  timeControl?: string;
  timeClass?: string;
  pgn: string;
  movesUci: string[];
  movesSan: string[];
};

export type AnalyzedMovePly = {
  plyIndex: number;
  playedUci: string;
  quality: MoveQuality;
};

export type MyGameDetail = {
  game: MyGameReplayPayload;
  source: ExtendedGameSource;
  analysisStatus: GameAnalysisStatus;
  mistakes: MistakePly[];
  analyzedMoves: AnalyzedMovePly[];
  userColor: 'w' | 'b' | null;
};

export type ImportStatus = {
  hasImported: boolean;
  importedGameCount: number;
  importGameLimit: number;
  atImportCap: boolean;
  analyzedGamesOlderThanWeekCount?: number;
  importJob: ImportJobDto | null;
};

export type MyGamesResult = {
  games: MyGameSummary[];
  offset: number;
  total?: number;
  hasMore?: boolean;
};

const myGameDetailInFlight = new Map<string, Promise<MyGameDetail>>();

export const fetchMyGames = async (params?: {
  limit?: number;
  offset?: number;
  includeTotal?: boolean;
}): Promise<MyGamesResult> => {
  const query: Record<string, string | number | boolean> = {};
  const limit = params?.limit ?? GAMES_BROWSE_PAGE_SIZE;
  query.limit = limit;
  if (params?.offset !== undefined) {
    query.offset = params.offset;
  }
  if (params?.includeTotal) {
    query.includeTotal = true;
  }

  const { data } = await gamesApi.get<{
    games: MyGameSummary[];
    offset: number;
    total?: number;
    hasMore?: boolean;
  }>('/mine', { params: query });
  return {
    games: data.games ?? [],
    offset: data.offset ?? params?.offset ?? 0,
    total: data.total,
    hasMore: data.hasMore,
  };
};

export const fetchMyGameDetail = async (
  gameId: string,
): Promise<MyGameDetail> => {
  const trimmed = gameId.trim();
  if (!trimmed) {
    throw new Error('gameId is required');
  }

  const inFlight = myGameDetailInFlight.get(trimmed);
  if (inFlight) {
    return inFlight;
  }

  const request = gamesApi
    .get<MyGameDetail>(`/mine/${encodeURIComponent(trimmed)}`)
    .then(({ data }) => data)
    .finally(() => {
      myGameDetailInFlight.delete(trimmed);
    });

  myGameDetailInFlight.set(trimmed, request);
  return request;
};

export const requestMyGameAnalysis = async (
  gameId: string,
): Promise<MyGameDetail> => {
  const { data } = await gamesApi.post<MyGameDetail>(
    `/mine/${encodeURIComponent(gameId)}/analyze`,
    {},
    { skipGlobalErrorToast: true },
  );
  return data;
};

export const cancelMyGameAnalysisSchedule = async (
  gameId: string,
): Promise<MyGameDetail> => {
  const { data } = await gamesApi.delete<MyGameDetail>(
    `/mine/${encodeURIComponent(gameId)}/analysis/schedule`,
    { skipGlobalErrorToast: true },
  );
  return data;
};

export const topUpMyGameAnalysisBuffer = async (): Promise<{ queued: number }> => {
  const { data } = await gamesApi.post<{ queued: number }>(
    '/mine/analysis/top-up',
    {},
    { skipGlobalErrorToast: true },
  );
  return data;
};

export type MyGameAnalysisStatusRow = {
  gameId: string;
  analysisStatus: GameAnalysisStatus;
  analysisRequestedAt: string;
  moveQualityCounts?: MoveQualityCounts;
};

export const fetchRecentMyGameAnalyses = async (): Promise<{
  games: MyGameAnalysisStatusRow[];
}> => {
  const { data } = await gamesApi.get<{ games: MyGameAnalysisStatusRow[] }>(
    '/mine/analysis/recent',
    { skipGlobalErrorToast: true },
  );
  return { games: data.games ?? [] };
};

export const markMyGameAnalysesNotified = async (
  gameIds: string[],
): Promise<{ notified: number }> => {
  const { data } = await gamesApi.post<{ notified: number }>(
    '/mine/analysis/notified',
    { gameIds },
    { skipGlobalErrorToast: true },
  );
  return data;
};

export const fetchImportStatus = async (): Promise<ImportStatus> => {
  const { data } = await importApi.get<ImportStatus>('/status');
  return data;
};

export type ImportPlatform = 'chesscom' | 'lichess';

export const submitImport = async (params: {
  platform: ImportPlatform;
  username: string;
  deleteOldAnalyzedGames?: boolean;
}): Promise<{ message: string }> => {
  const { data } = await importApi.post<{ message: string }>(
    '/',
    {
      other_platform: params.platform,
      other_username: params.username.trim(),
      deleteOldAnalyzedGames: params.deleteOldAnalyzedGames !== false,
    },
    { skipGlobalErrorToast: true },
  );
  return data;
};

export const deleteMyGame = async (
  gameId: string,
): Promise<{ deleted: boolean }> => {
  const { data } = await gamesApi.delete<{ deleted: boolean }>(
    `/mine/${encodeURIComponent(gameId)}`,
    { skipGlobalErrorToast: true },
  );
  return data;
};

export const getMyGamesApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => getApiErrorMessage(error, { action: fallback });
